
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Backdrop,
} from "@mui/material";
import {
  Warning as WarningIcon,
  Print as PrintIcon,
  Close as CloseIcon,
  Launch as LaunchIcon,
  ShoppingCart as ShoppingCartIcon,
  Inventory as InventoryIcon,
} from "@mui/icons-material";
import { useModalStore } from "../context/modalStore";
import { ComponentModal } from "../common/interfaces";

interface NotificationProps {
  message: string;
  onClose: () => void;
}

const NotificationSound: React.FC<NotificationProps> = ({ message, onClose }) => {
  const [audio] = useState(() => new Audio("/sound/alerta.mp3"));
  const { openModal } = useModalStore();

  useEffect(() => {
    audio.loop = true;
    audio
      .play()
      .catch((err) => console.error("Error playing alert sound:", err));

    return () => {
      audio.pause();
      audio.currentTime = 0; // Reset sound
    };
  }, [audio]);

  // Parse the message to extract title and content
  const lines = message.split('\n').filter(line => line.trim());
  const title = lines[0] || "Notificación";
  const content = lines.slice(1).join('\n');

  // Detect notification type based on message content
  const isPriceUpdate = message.includes('ACTUALIZACIÓN DE PRECIOS') || message.includes('actualizaron precios');
  const isOnlineOrder = message.includes('Pedido de tienda en línea') || message.includes('tienda en línea');
  const isInventoryTransfer = message.includes('transferencia') || message.includes('traspaso');

  // Get appropriate icon and color based on notification type
  const getNotificationConfig = () => {
    if (isPriceUpdate) {
      return {
        icon: <PrintIcon />,
        color: '#ff9800', // Orange for price updates
        showPrintInfo: true,
        showModuleButton: true,
        moduleText: 'Ir al Módulo',
        moduleAction: () => openModal(ComponentModal.PriceUpdates)
      };
    } else if (isOnlineOrder) {
      return {
        icon: <ShoppingCartIcon />,
        color: '#2196f3', // Blue for online orders
        showPrintInfo: false,
        showModuleButton: false,
        moduleText: '',
        moduleAction: () => {}
      };
    } else if (isInventoryTransfer) {
      return {
        icon: <InventoryIcon />,
        color: '#4caf50', // Green for inventory transfers
        showPrintInfo: false,
        showModuleButton: false,
        moduleText: '',
        moduleAction: () => {}
      };
    } else {
      return {
        icon: <WarningIcon />,
        color: '#ff9800', // Default orange
        showPrintInfo: false,
        showModuleButton: false,
        moduleText: '',
        moduleAction: () => {}
      };
    }
  };

  const config = getNotificationConfig();

  const handleGoToModule = () => {
    onClose();
    config.moduleAction();
  };

  return (
    <>
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.8)'
        }}
        open={true}
      />
      <Dialog
        open={true}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 2,
          '& .MuiDialog-paper': {
            borderRadius: 2,
            border: `2px solid ${config.color}`,
          }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: config.color,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          {config.icon}
          <Typography variant="h6" component="span">
            {title}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Alert
            severity={isPriceUpdate ? "warning" : isOnlineOrder ? "info" : isInventoryTransfer ? "success" : "warning"}
            sx={{ mb: 2 }}
          >
            <Typography variant="body1" component="div" sx={{ whiteSpace: 'pre-line' }}>
              {content}
            </Typography>
          </Alert>

          {config.showPrintInfo && (
            <Box display="flex" alignItems="center" gap={1} mt={2}>
              <PrintIcon color="primary" />
              <Typography variant="body2" color="textSecondary">
                Usa el módulo de "Actualizaciones de Precios" para imprimir las etiquetas necesarias.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            startIcon={<CloseIcon />}
            sx={{ flex: config.showModuleButton ? 1 : 2 }}
          >
            Cerrar
          </Button>
          {config.showModuleButton && (
            <Button
              onClick={handleGoToModule}
              variant="contained"
              color="primary"
              startIcon={<LaunchIcon />}
              sx={{ flex: 1 }}
            >
              {config.moduleText}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NotificationSound;
